import React, { useEffect, useState } from "react";
import { BaseApi } from "../base.api";
import { Button, Col, Input, Row, Select, Spin, Table, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";

// Define the type for a Company
interface Company {
  id: number;
  name: string;
  description: string;
  status: number;
}
interface CompanyStatus {
  status: number;
  title: string;
  color: string;
}

const Companies: React.FC = () => {
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>("");
  const [subsType, setSubsType] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState(1);

  useEffect(() => {
    loadCompanies();
  }, []);
  useEffect(() => {
    if (allCompanies.length > 0) {
      handleChange(selectedStatus);
    }
  }, [allCompanies]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const response = await new BaseApi().fetchData(
        "/user-service-v2/companies"
      );
      if (response?.data) {
        response.data.forEach((element: any) => {
          const deadlineDate = new Date(
            element.subscription.subscriptionDeadline
          );
          const diffTime = deadlineDate.getTime() - new Date().getTime();
          const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
          element.daysUntilDeadline = diffDays;
          element.endDayStr = deadlineDate.toString().slice(0, 10);
        });

        setCompanies(response?.data);
        setAllCompanies(response?.data);
      }

      setLoading(false);
    } catch (error: any) {
      alert(error.message);
      setLoading(false);
    }
  };

  const companyStatus = [
    {
      status: 0,
      title: "Pending",
      color: "blue",
    },
    {
      status: 1,
      title: "Active",
      color: "green",
    },
    {
      status: 2,
      title: "Deactive",
      color: "orange",
    },
    {
      status: 3,
      title: "Deleted",
      color: "red",
    },
  ];

  const columns = [
    {
      title: "Name",
      key: "name",

      render: (record: any) => (
        <a href={"/main/companies/" + record._id}>{record.name || "NO NAME"}</a>
      ),
    },
    {
      title: "Domain",
      render: (record: any) => (
        <a href={"https://" + record.domain} target="_blank">
          {record.domain}
        </a>
      ),
      key: "domain",
    },
    {
      title: "Insert date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (record: any) => {
        const time = new Date(record);

        return time.toLocaleString();
      },
    },
    {
      title: "Plan",
      key: "plan",
      render: (record: any) => (
        <Tag>{getPlan(record.subscription.subscriptionType)}</Tag>
      ),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (record: any) => {
        const company = companyStatus.find(
          (item: CompanyStatus) => item.status === record
        );
        return <Tag color={company?.color}>{company?.title}</Tag>;
      },
    },
    {
      title: "Subsription ends",
      key: "subscription",
      render: (record: any) =>
        record.daysUntilDeadline < 0 ? (
          <Tag color="red">
            {record.endDayStr} / {record.daysUntilDeadline} days
          </Tag>
        ) : (
          <Tag color="green">
            {record.endDayStr} / {record.daysUntilDeadline} days
          </Tag>
        ),
    },
    {
      key: "parentId",
      render: (record: any) => {
        if (record.parentId) {
          const parentCompany = companies.find(
            (company: any) => company._id === record.parentId
          );
          return parentCompany ? (
            <a href={"/main/companies/" + record.parentId}>
              <Button>{parentCompany.name}</Button>
            </a>
          ) : (
            ""
          );
        }
      },
    },
  ];

  const onPlanChange = (e: any) => {
    setSubsType(e);
    filter(e, searchText, selectedStatus);
  };

  const handleSearch = (val: string) => {
    setSearchText(val);
    filter(subsType, val, selectedStatus);
  };
  const handleChange = async (status: number) => {
    setSelectedStatus(status);
    filter(subsType, searchText, status);
  };

  const filter = (type: string, searchText: string, selectedStatus: number) => {
    const regex = new RegExp(searchText, "i");

    const filtered = allCompanies.filter((company: any) => {
      const matchesSearch =
        !searchText || regex.test(company.name) || regex.test(company.domain);

      const matchesType =
        !type || Number(type) < 1 || isNaN(Number(type))
          ? true
          : company.subscription.subscriptionType === Number(type);

      const matchesStatus =
        selectedStatus === 4
          ? company.daysUntilDeadline < 0
          : company.status === Number(selectedStatus);

      return matchesSearch && matchesType && matchesStatus;
    });
    setCompanies(filtered);
  };

  const getPlan = (type: number) => {
    switch (type) {
      case 1:
        return "Trial";
      case 2:
        return "Monthly";
      case 3:
        return "Yearly";
      case 4:
        return "PayAsYouGo";
      case 5:
        return "MonthlyUnlimited";
      case 6:
        return "YearlyUnlimited";
      case 7:
        return "Hybrid";
      case 8:
        return "Flex";
      default:
        return "Unknown Subscription Type";
    }
  };

  return (
    <div>
      <h1>Companies</h1>
      <Row style={{ marginBottom: "20px" }}>
        <Col xs={12} lg={6}>
          <Input
            placeholder="Write to search.."
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </Col>
        <Col xs={12} lg={6}>
          <Select
            style={{ width: "100%" }}
            onChange={onPlanChange}
            placeholder="Select plan"
            allowClear
          >
            <Select.Option value="1">Trial</Select.Option>
            <Select.Option value="2">Monthly</Select.Option>
            <Select.Option value="3">Yearly</Select.Option>
            <Select.Option value="4">PayAsYouGo</Select.Option>
            <Select.Option value="5">Monthly Unlimited</Select.Option>
            <Select.Option value="6">Yearly Unlimited</Select.Option>
            <Select.Option value="7">Hybrid</Select.Option>
            <Select.Option value="8">Flex</Select.Option>
          </Select>
        </Col>
        <Col xs={12} lg={6}>
          <Select
            defaultValue={selectedStatus}
            value={selectedStatus}
            style={{ width: 120 }}
            onChange={handleChange}
            options={[
              { value: 1, label: "Active" },
              { value: 2, label: "Deactive" },
              { value: 0, label: "Pending" },
              { value: 3, label: "Deleted" },
              { value: 4, label: "Expired" },
            ]}
          />
        </Col>
      </Row>
      <Table
        dataSource={companies}
        columns={columns}
        scroll={{ x: true }}
        rowKey="_id"
        loading={loading}
      />
    </div>
  );
};

export default Companies;
